import type { IFiltroPaginador, IRespuestaApiLista, IRespuestaApiObjeto } from "../../globales/interfaces";
import api from "../../utils/api";
import type { IProyectoNBS } from "./entidades/IProyectoNBS";
import type { IEquipo } from "./entidades/IEquipo";
import type { IPaisNBS } from "./entidades/IPaisNBS";
import type { EstadoCotizacionNBSId } from "./entidades/EstadoCotizacionNBS";
import type { IResultadoCargaMasiva, MatrizCargaMasiva } from "../../modulos/configuracionCotizadorNbs/interfaces";
import { saveAs } from "file-saver";

/** Archivo traído del back, listo para previsualizar o guardar. */
export interface IArchivoDescargado {
    nombreArchivo: string;
    contenido: Blob;
}

/** Guarda en el equipo del usuario un archivo ya descargado. */
export const guardarArchivo = ({ contenido, nombreArchivo }: IArchivoDescargado) => saveAs(contenido, nombreArchivo);

export interface IResultadoGenerarCotizacion {
    CotizacionId: string;
    EstaCompleto: boolean;
    FormulasPendientes: string[];
    /** Para el usuario: qué falta en las matrices o en el equipo y dónde corregirlo, y avisos (p. ej. comisión en 0%). */
    Mensajes: string[];
}

/** Un cambio de estado de la cotización (api/cotizaciones/{id}/historial-estados). Fecha en UTC. */
/** Un cambio en una variable de cálculo (configuración global o de una cotización). Fecha en UTC; valores sin formato. */
export interface ICambioVariable {
    Id: number;
    /** Nombre de la propiedad (TarifaHoraHombre, MargenLocalAscensor, ...): sirve para saber la unidad. */
    Clave: string;
    Variable: string;
    ValorAnterior: string | null;
    ValorNuevo: string | null;
    Usuario: string;
    Fecha: string;
}

export interface IHistorialEstadoCotizacion {
    Id: number;
    EstadoAnterior: number | null;
    EstadoNuevo: number;
    Usuario: string;
    Fecha: string;
}

export interface IOpcionSelect {
    value: string;
    label: string;
}

/** Capacidad de ascensor (value = kg) con su número de pasajeros. */
export interface IOpcionCapacidad extends IOpcionSelect {
    pasajeros: number;
}

export interface IOpcionesEquiposNBS {
    ascensores: {
        tipos: IOpcionSelect[];
        usos: IOpcionSelect[];
        capacidades: IOpcionCapacidad[];
        productos: IOpcionSelect[];
        velocidades: IOpcionSelect[];
        operadoresPuerta: IOpcionSelect[];
        maquinas: IOpcionSelect[];
        /** Ids de máquina permitidos por id de producto. Un producto que no está acepta cualquier máquina. */
        maquinasPorProducto: Record<string, string[]>;
        maniobras: IOpcionSelect[];
        aperturas: IOpcionSelect[];
    };
    escaleras: {
        tipos: IOpcionSelect[];
        productos: IOpcionSelect[];
        velocidades: IOpcionSelect[];
        anchosPasos: IOpcionSelect[];
        inclinaciones: IOpcionSelect[];
    };
}

interface ICatalogoItemBackend {
    Id: number;
    Nombre: string;
}

interface ICatalogosEquiposBackend {
    Ascensores: {
        Tipos: string[];
        Usos: string[];
        Capacidades: { CapacidadKg: number; Pasajeros: number }[];
        Productos: ICatalogoItemBackend[];
        Velocidades: string[];
        OperadoresPuerta: ICatalogoItemBackend[];
        Maquinas: ICatalogoItemBackend[];
        MaquinasPorProducto?: { ProductoId: number; MaquinaIds: number[] }[];
        Maniobras?: string[];
        Aperturas?: string[];
    };
    Escaleras: {
        Tipos: string[];
        Productos: ICatalogoItemBackend[];
        Velocidades: string[];
        AnchosPasos: string[];
        Inclinaciones: string[];
    };
}

export class CotizadorModelo {
    // 

    constructor() { }
    
    /** usuarioCreadorId: solo las cotizaciones de ese usuario (asesores comerciales sin permiso de ver todas). */
    public async obtenerListaCotizaciones(page: number = 1, size: number = 20, paisId?: number, estadoId?: number, usuarioCreadorId?: number): Promise<IRespuestaApiLista<any>> {
        let queryParams = `?page=${page}&size=${size}`;
        if (paisId) queryParams += `&paisId=${paisId}`;
        if (estadoId) queryParams += `&estado=${estadoId}`;
        if (usuarioCreadorId) queryParams += `&usuarioCreadorId=${usuarioCreadorId}`;
        
        const { data } = await api.get<IRespuestaApiLista<any>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones${queryParams}`);
        return data;
    }

    // --- CONFIGURACIÓN GLOBAL ---
    /** Países del cotizador (Colombia, Panamá y Costa Rica). */
    public async obtenerPaises(): Promise<IRespuestaApiLista<IPaisNBS>> {
        const { data } = await api.get<IRespuestaApiLista<IPaisNBS>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/paises`);
        return data;
    }

    /** País del cotizador (moneda local y si usa TRM). */
    public async obtenerPais(paisId: number): Promise<IRespuestaApiObjeto<IPaisNBS>> {
        const { data } = await api.get<IRespuestaApiObjeto<IPaisNBS>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/paises/${paisId}`);
        return data;
    }

    public async obtenerConfiguracionGlobal(paisId: number): Promise<IRespuestaApiObjeto<any>> {
        const { data } = await api.get<IRespuestaApiObjeto<any>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/configuracion/activa/${paisId}`);
        return data;
    }

    public async guardarConfiguracionGlobal(configuracion: any): Promise<IRespuestaApiObjeto<number>> {
        const { data } = await api.post<IRespuestaApiObjeto<number>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/configuracion`, configuracion);
        return data;
    }

    // --- MATRICES ---
    public async obtenerMatrices<T>(endpoint: string, paisId?: number): Promise<IRespuestaApiObjeto<T[]>> {
        const queryParams = paisId ? `?paisId=${paisId}` : '';
        const { data } = await api.get<IRespuestaApiObjeto<T[]>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/${endpoint}${queryParams}`);
        return data;
    }

    /** Página de una matriz (page empieza en 1); el total viene en Pagination. */
    /** filtros: parámetros adicionales de la consulta (p. ej. productoId, maquinaId); los vacíos no se envían. */
    public async obtenerMatricesPaginado<T>(endpoint: string, paisId: number | undefined, paginador: IFiltroPaginador,
        filtros: Record<string, number | undefined> = {}): Promise<IRespuestaApiLista<T>> {
        const extra = Object.entries(filtros).filter(([, valor]) => valor !== undefined).map(([clave, valor]) => `&${clave}=${valor}`).join('');
        const queryParams = `?page=${paginador.Page}&size=${paginador.Size}${paisId ? `&paisId=${paisId}` : ''}${extra}`;
        const { data } = await api.get<IRespuestaApiLista<T>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/${endpoint}${queryParams}`);
        return data;
    }

    public async guardarMatrizFila<T>(endpoint: string, id: number | null, matriz: T): Promise<IRespuestaApiObjeto<any>> {
        if (id && id > 0) {
            const { data } = await api.put<IRespuestaApiObjeto<any>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/${endpoint}/${id}`, matriz);
            return data;
        } else {
            const { data } = await api.post<IRespuestaApiObjeto<any>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/${endpoint}`, matriz);
            return data;
        }
    }

    /** Descarga la plantilla Excel de carga masiva de una matriz (el back la arma con los catálogos vigentes). */
    public async descargarPlantillaMatriz(matriz: MatrizCargaMasiva, paisId?: number): Promise<void> {
        const queryParams = paisId ? `?paisId=${paisId}` : '';
        const archivo = await this.obtenerArchivo(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/${matriz}/plantilla${queryParams}`, `Plantilla_${matriz}.xlsx`);
        guardarArchivo(archivo);
    }

    /** Genera la Oferta Comercial en PDF (requiere la cotización calculada y completa). Tarda unos segundos:
     *  quien la previsualiza debe reutilizar este archivo para descargarla, no pedirla de nuevo. */
    public async obtenerPdfCotizacion(cotizacionId: string): Promise<IArchivoDescargado> {
        return this.obtenerArchivo(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/pdf`, "Propuesta Comercial.pdf", "application/pdf");
    }

    /** Trae un archivo del back con el nombre que envía en Content-Disposition. */
    private async obtenerArchivo(url: string, nombrePorDefecto: string, tipo?: string): Promise<IArchivoDescargado> {
        let response;
        try {
            response = await api.get(url, { responseType: "blob" });
        } catch (error: any) {
            // Con responseType blob el error también llega como blob: se lee para mostrar el mensaje del back.
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try {
                    const cuerpo = JSON.parse(await data.text());
                    if (Array.isArray(cuerpo?.Messages) && cuerpo.Messages.length > 0) error.mensajeBackend = cuerpo.Messages.join(' ');
                } catch {
                    // No era JSON: se deja el mensaje genérico de quien llama.
                }
            }
            throw error;
        }
        // Con espacios o tildes el back envía filename (ASCII) y filename* (UTF-8): se prefiere filename*.
        const disposition: string | undefined = response.headers["content-disposition"];
        const utf8 = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
        const simple = disposition?.match(/filename="?([^;"]+)"?/i)?.[1];
        const nombreArchivo = utf8 ? decodeURIComponent(utf8) : simple ?? nombrePorDefecto;
        return { nombreArchivo, contenido: new Blob([response.data], tipo ? { type: tipo } : undefined) };
    }

    /** Carga masiva de una matriz: guarda las filas válidas y devuelve las rechazadas con sus errores. */
    /** reemplazar: además borra las filas que no vienen en el archivo. simular: solo cuenta, no guarda. */
    public async cargaMasivaMatriz(matriz: MatrizCargaMasiva, paisId: number, archivo: File, reemplazar = false, simular = false): Promise<IRespuestaApiObjeto<IResultadoCargaMasiva>> {
        const formData = new FormData();
        formData.append("archivo", archivo);
        const { data } = await api.post<IRespuestaApiObjeto<IResultadoCargaMasiva>>(
            `${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/${matriz}/carga-masiva?paisId=${paisId}&reemplazar=${reemplazar}&simular=${simular}`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
        return data;
    }

    /** Nombres de las cotizaciones abiertas que usan una fila de matriz (para avisar antes de borrarla). */
    public async obtenerUsoFilaMatriz(matriz: 'logistica' | 'moi-escalera' | 'comision', id: number): Promise<IRespuestaApiObjeto<string[]>> {
        const { data } = await api.get<IRespuestaApiObjeto<string[]>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/${matriz}/${id}/uso`);
        return data;
    }

    /** MOI Ascensores se edita por fila de paradas: cotizaciones abiertas con ascensores de esas paradas. */
    public async obtenerUsoMoiAscensor(paisId: number, paradas: number): Promise<IRespuestaApiObjeto<string[]>> {
        const { data } = await api.get<IRespuestaApiObjeto<string[]>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/moi-ascensor/uso?paisId=${paisId}&paradas=${paradas}`);
        return data;
    }

    public async eliminarMatrizFila(endpoint: string, id: number): Promise<IRespuestaApiObjeto<any>> {
        const { data } = await api.delete<IRespuestaApiObjeto<any>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/${endpoint}/${id}`);
        return data;
    }

    /** Guarda la matriz completa. paisId: obligatorio para moi-ascensor (la matriz puede ir vacía). */
    public async guardarMatrizMasiva<T>(endpoint: string, matrices: T[], paisId?: number): Promise<IRespuestaApiObjeto<any>> {
        const queryParams = paisId ? `?paisId=${paisId}` : '';
        const { data } = await api.post<IRespuestaApiObjeto<any>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/matrices/${endpoint}${queryParams}`, matrices);
        return data;
    }

    /** Todas las listas del formulario de equipos, desde el back (api/catalogos/equipos). */
    public async obtenerOpcionesEquipos(): Promise<IOpcionesEquiposNBS> {
        const { data } = await api.get<IRespuestaApiObjeto<ICatalogosEquiposBackend>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/catalogos/equipos`);
        const catalogos = data.Data;

        const porId = (items?: { Id: number; Nombre: string }[]) => (items ?? []).map(item => ({ value: String(item.Id), label: item.Nombre }));
        // Mantine no admite opciones con el mismo value: si el catálogo trae repetidos (p. ej. "1.0" y "1.00"), queda el primero.
        const sinRepetidos = (opciones: IOpcionSelect[]) => opciones.filter((o, i) => opciones.findIndex(x => x.value === o.value) === i);
        const porValor = (valores?: string[]) => sinRepetidos((valores ?? []).map(valor => ({ value: valor, label: valor })));
        // Los valores numéricos se guardan como número: el value debe coincidir con String(numero) del formulario.
        const numericos = (valores?: string[]) => sinRepetidos((valores ?? []).map(valor => ({ value: String(Number(valor)), label: valor })));

        return {
            ascensores: {
                tipos: porValor(catalogos?.Ascensores?.Tipos),
                usos: porValor(catalogos?.Ascensores?.Usos),
                capacidades: (catalogos?.Ascensores?.Capacidades ?? []).map(c => ({
                    value: String(c.CapacidadKg), label: `${c.CapacidadKg} kg · ${c.Pasajeros} pasajeros`, pasajeros: c.Pasajeros
                })),
                productos: porId(catalogos?.Ascensores?.Productos),
                velocidades: numericos(catalogos?.Ascensores?.Velocidades),
                operadoresPuerta: porId(catalogos?.Ascensores?.OperadoresPuerta),
                maquinas: porId(catalogos?.Ascensores?.Maquinas),
                maquinasPorProducto: Object.fromEntries((catalogos?.Ascensores?.MaquinasPorProducto ?? [])
                    .map(pm => [String(pm.ProductoId), pm.MaquinaIds.map(String)])),
                maniobras: porValor(catalogos?.Ascensores?.Maniobras),
                aperturas: porValor(catalogos?.Ascensores?.Aperturas)
            },
            escaleras: {
                tipos: porValor(catalogos?.Escaleras?.Tipos),
                productos: porId(catalogos?.Escaleras?.Productos),
                velocidades: numericos(catalogos?.Escaleras?.Velocidades),
                anchosPasos: numericos(catalogos?.Escaleras?.AnchosPasos),
                inclinaciones: numericos(catalogos?.Escaleras?.Inclinaciones)
            }
        };
    }

    /** Productos del catálogo filtrados por tipo de equipo (0 = Ascensor, 1 = Escalera). */
    public async obtenerProductos(tipoEquipo?: number): Promise<{ value: string; label: string }[]> {
        const queryParams = tipoEquipo !== undefined ? `?tipoEquipo=${tipoEquipo}` : '';
        const { data } = await api.get<IRespuestaApiObjeto<{ Id: number; Nombre: string }[]>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/catalogos/productos${queryParams}`);
        return (data.Data ?? []).map(item => ({ value: String(item.Id), label: item.Nombre }));
    }

    public async crearCotizacionPaso1(datos: IProyectoNBS): Promise<IRespuestaApiObjeto<string>> {
        const { data } = await api.post<IRespuestaApiObjeto<string>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones`, datos);
        return data; 
    }

    public async actualizarCotizacion(cotizacionId: string, datos: any): Promise<IRespuestaApiObjeto<boolean>> {
        const { data } = await api.put<IRespuestaApiObjeto<boolean>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}`, datos);
        return data; 
    }

    public async agregarEquipo(cotizacionId: string, equipo: IEquipo): Promise<IRespuestaApiObjeto<any>> {
        const { data } = await api.post<IRespuestaApiObjeto<any>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/equipos`, equipo);
        return data; 
    }

    public async editarEquipo(cotizacionId: string, equipoId: string, equipo: IEquipo): Promise<IRespuestaApiObjeto<any>> {
        const { data } = await api.put<IRespuestaApiObjeto<any>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/equipos/${equipoId}`, equipo);
        return data; 
    }

    public async eliminarEquipo(cotizacionId: string, equipoId: string): Promise<IRespuestaApiObjeto<boolean>> {
        const { data } = await api.delete<IRespuestaApiObjeto<boolean>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/equipos/${equipoId}`);
        return data; 
    }

    /** Calcula la cotización (al llegar al Resumen). Si queda completa y estaba en Borrador pasa a Revisión Interna. */
    public async generarCotizacion(cotizacionId: string): Promise<IRespuestaApiObjeto<IResultadoGenerarCotizacion>> {
        const { data } = await api.post<IRespuestaApiObjeto<IResultadoGenerarCotizacion>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/generar`);
        return data;
    }

    /** Corrige a mano los contenedores de un ascensor (null = volver al calculado); el back recalcula la cotización. */
    public async actualizarContenedoresEquipo(cotizacionId: string, equipoId: string, cantidadContenedores: number | null): Promise<IRespuestaApiObjeto<IResultadoGenerarCotizacion>> {
        const { data } = await api.put<IRespuestaApiObjeto<IResultadoGenerarCotizacion>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/equipos/${equipoId}/contenedores`, { CantidadContenedores: cantidadContenedores });
        return data;
    }

    /** Guarda variables de configuración solo para esta cotización (la global no cambia); el back recalcula. */
    public async guardarConfiguracionCotizacion(cotizacionId: string, configuracion: any): Promise<IRespuestaApiObjeto<IResultadoGenerarCotizacion>> {
        const { data } = await api.put<IRespuestaApiObjeto<IResultadoGenerarCotizacion>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/configuracion`, configuracion);
        return data;
    }

    /** La cotización vuelve a la configuración global vigente (descarta la propia); el back recalcula. */
    public async usarConfiguracionGlobal(cotizacionId: string): Promise<IRespuestaApiObjeto<IResultadoGenerarCotizacion>> {
        const { data } = await api.post<IRespuestaApiObjeto<IResultadoGenerarCotizacion>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/configuracion/usar-global`);
        return data;
    }

    /** Quién cambió qué variable de la configuración global del país y cuándo. */
    public async obtenerCambiosConfiguracionGlobal(paisId: number): Promise<IRespuestaApiObjeto<ICambioVariable[]>> {
        const { data } = await api.get<IRespuestaApiObjeto<ICambioVariable[]>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/configuracion/cambios/${paisId}`);
        return data;
    }

    /** Quién cambió qué variable de la cotización y cuándo. */
    public async obtenerCambiosVariablesCotizacion(cotizacionId: string): Promise<IRespuestaApiObjeto<ICambioVariable[]>> {
        const { data } = await api.get<IRespuestaApiObjeto<ICambioVariable[]>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/cambios-variables`);
        return data;
    }

    public async obtenerHistorialEstados(cotizacionId: string): Promise<IRespuestaApiObjeto<IHistorialEstadoCotizacion[]>> {
        const { data } = await api.get<IRespuestaApiObjeto<IHistorialEstadoCotizacion[]>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/historial-estados`);
        return data;
    }

    /** Cambio manual de estado. El back valida que la transición sea permitida. */
    public async cambiarEstadoCotizacion(cotizacionId: string, estado: EstadoCotizacionNBSId): Promise<IRespuestaApiObjeto<boolean>> {
        const { data } = await api.put<IRespuestaApiObjeto<boolean>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}/estado`, { Estado: estado });
        return data;
    }

    public async obtenerCotizacion(cotizacionId: string): Promise<IRespuestaApiObjeto<any>> {
        const { data } = await api.get<IRespuestaApiObjeto<any>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}`);
        return data;
    }

    public async eliminarCotizacion(cotizacionId: string): Promise<IRespuestaApiObjeto<boolean>> {
        const { data } = await api.delete<IRespuestaApiObjeto<boolean>>(`${import.meta.env.VITE_API_URL_COTIZADORNBS}/api/cotizaciones/${cotizacionId}`);
        return data;
    }
}
